import React, { useEffect, useState } from "react";
import {
  HiX,
  HiOutlineClipboardCheck,
  HiOutlineLocationMarker,
  HiOutlineUser,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";

import {
  submitIncident,
  getIncidents,
} from "../../../features/incidents/incidentActions.js";

import { getUsersAsync } from "../../../features/users/usersActions.js";

export default function CorrectiveActionWorkflow({ incident, onClose, refreshList = true }) {
  const dispatch = useDispatch();

  // ============================================================
  // USERS FROM REDUX
  // ============================================================

  const {
    users = [],
    loading: usersLoading,
    error: usersError,
  } = useSelector((state) => state.users);

  // ============================================================
  // FORM STATE
  // ============================================================

  const [status, setStatus] = useState(String(incident?.status || "").trim().toUpperCase());

  const [referredToId, setReferredToId] = useState(
    incident?.referredToId ? String(incident.referredToId) : "",
  );

  const [correctiveAction, setCorrectiveAction] = useState(
    incident?.correctiveAction || "",
  );

  const [rootCause, setRootCause] = useState(incident?.rootCause || "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ============================================================
  // FETCH USERS
  // ============================================================

  useEffect(() => {
    dispatch(getUsersAsync());
  }, [dispatch]);

  // ============================================================
  // RESET FORM WHEN INCIDENT CHANGES
  // ============================================================

  useEffect(() => {
    setStatus(String(incident?.status || "").trim().toUpperCase());

    setReferredToId(
      incident?.referredToId ? String(incident.referredToId) : "",
    );

    setCorrectiveAction(incident?.correctiveAction || "");

    setRootCause(incident?.rootCause || "");

    setError("");
  }, [incident]);

  // ============================================================
  // SUBMIT / UPDATE INCIDENT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!incident?.id) {
      setError("Incident ID is missing.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        id: incident.id,
        status,
        referredToId: referredToId === "" ? null : Number(referredToId),
        correctiveAction: correctiveAction.trim() || null,
        rootCause: rootCause.trim() || null,
      };

      await dispatch(submitIncident(payload)).unwrap();

      if (refreshList) await dispatch(getIncidents()).unwrap();

      onClose();
    } catch (err) {
      console.error("Failed to save corrective action:", err);

      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to save corrective action.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // NO INCIDENT
  // ============================================================

  if (!incident) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div
        className="
          flex
          flex-col
          w-full
          max-w-2xl
          max-h-[90vh]
          overflow-hidden
          rounded-2xl
          sm:rounded-3xl
          bg-white
          shadow-2xl
        "
      >
        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="shrink-0 flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
              <HiOutlineClipboardCheck className="w-5 h-5 text-emerald-600" />
            </div>

            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                Corrective Action
              </h2>

              <p className="mt-0.5 text-xs text-gray-500">
                Incident #{incident.id}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="
              shrink-0
              p-2
              rounded-xl
              text-gray-400
              hover:text-gray-600
              hover:bg-gray-100
              transition-colors
              disabled:opacity-50
            "
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* =====================================================
            SCROLLABLE CONTENT
        ====================================================== */}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {/* ===================================================
              INCIDENT SUMMARY
          ==================================================== */}

          <div className="px-4 pt-4 sm:px-6 sm:pt-5">
            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="flex items-start gap-3">
                <HiOutlineClipboardCheck className="mt-0.5 w-4 h-4 shrink-0 text-gray-400" />

                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    Incident
                  </p>

                  <p className="mt-1 text-sm font-semibold text-gray-900 break-words">
                    {incident.title}
                  </p>

                  {incident.location && (
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                      <HiOutlineLocationMarker className="w-3.5 h-3.5 shrink-0" />

                      <span className="break-words">
                        {incident.location}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===================================================
              ERROR
          ==================================================== */}

          {(error || usersError) && (
            <div className="mx-4 mt-4 sm:mx-6 sm:mt-5 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600">
              {error || usersError}
            </div>
          )}

          {/* ===================================================
              FORM
          ==================================================== */}

          <form
            onSubmit={handleSubmit}
            className="p-4 sm:p-6 space-y-5"
          >
            {/* =================================================
                STATUS + REFERRED TO
            ================================================== */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* STATUS */}

              <div>
                <label
                  htmlFor="corrective-action-status"
                  className="block mb-2 text-xs font-bold text-gray-700"
                >
                  Status
                </label>

                <select
                  id="corrective-action-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={saving}
                  className="
                    w-full
                    px-3
                    py-2.5
                    border
                    border-gray-200
                    rounded-xl
                    bg-white
                    text-sm
                    text-gray-700
                    focus:outline-none
                    focus:border-blue-500
                    disabled:bg-gray-50
                  "
                >
                  <option  value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>

              {/* REFERRED TO */}

              <div>
                <label
                  htmlFor="corrective-action-referred-to"
                  className="block mb-2 text-xs font-bold text-gray-700"
                >
                  Referred To
                </label>

                <div className="relative">
                  <HiOutlineUser
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      w-4
                      h-4
                      text-gray-400
                      pointer-events-none
                    "
                  />

                  <select
                    id="corrective-action-referred-to"
                    value={referredToId}
                    onChange={(e) => setReferredToId(e.target.value)}
                    disabled={saving || usersLoading}
                    className="
                      w-full
                      pl-9
                      pr-3
                      py-2.5
                      border
                      border-gray-200
                      rounded-xl
                      bg-white
                      text-sm
                      text-gray-700
                      focus:outline-none
                      focus:border-blue-500
                      disabled:bg-gray-50
                    "
                  >
                    <option value="">
                      {usersLoading
                        ? "Loading users..."
                        : "Select person"}
                    </option>

                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* =================================================
                CORRECTIVE ACTION
            ================================================== */}

            <div>
              <label
                htmlFor="corrective-action"
                className="block mb-2 text-xs font-bold text-gray-700"
              >
                Corrective Action
              </label>

              <textarea
                id="corrective-action"
                value={correctiveAction}
                onChange={(e) => setCorrectiveAction(e.target.value)}
                rows={5}
                disabled={saving}
                placeholder="Describe the corrective action taken or required..."
                className="
                  w-full
                  px-3
                  py-3
                  border
                  border-gray-200
                  rounded-xl
                  bg-white
                  text-sm
                  text-gray-700
                  placeholder-gray-400
                  resize-none
                  focus:outline-none
                  focus:border-blue-500
                  disabled:bg-gray-50
                "
              />
            </div>

            {/* =================================================
                ROOT CAUSE
            ================================================== */}

            <div>
              <label
                htmlFor="root-cause"
                className="block mb-2 text-xs font-bold text-gray-700"
              >
                Root Cause
              </label>

              <textarea
                id="root-cause"
                value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                rows={5}
                disabled={saving}
                placeholder="Describe the root cause of the incident..."
                className="
                  w-full
                  px-3
                  py-3
                  border
                  border-gray-200
                  rounded-xl
                  bg-white
                  text-sm
                  text-gray-700
                  placeholder-gray-400
                  resize-none
                  focus:outline-none
                  focus:border-blue-500
                  disabled:bg-gray-50
                "
              />
            </div>

            {/* =================================================
                ACTIONS
            ================================================== */}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="
                  w-full
                  sm:w-auto
                  px-4
                  py-2.5
                  rounded-xl
                  border
                  border-gray-200
                  bg-white
                  text-sm
                  font-semibold
                  text-gray-600
                  hover:bg-gray-50
                  transition-colors
                  disabled:opacity-50
                "
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="
                  w-full
                  sm:w-auto
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  px-5
                  py-2.5
                  rounded-xl
                  bg-emerald-600
                  text-white
                  text-sm
                  font-bold
                  hover:bg-emerald-700
                  transition-colors
                  disabled:opacity-60
                  disabled:cursor-not-allowed
                "
              >
                <HiOutlineCheckCircle className="w-4 h-4" />

                {saving ? "Saving..." : "Save Corrective Action"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
